import React, { useEffect, useRef, useState } from "react";

import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from "@mui/material";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

import API from "../services/api";

const emptyForm = {
  title: "",
  description: "",
  content: "",
  image: ""
};

const AdminNews = () => {

  // =====================================================
  // 📰 NOTICIAS
  // =====================================================

  const [newsList, setNewsList] = useState([]);

  const [loadingNews, setLoadingNews] = useState(true);

  // =====================================================
  // ✏️ EDITOR
  // =====================================================

  const [form, setForm] = useState(emptyForm);

  const [selectedImage, setSelectedImage] = useState(null);

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState("");

  const contentRef = useRef(null);


  // =====================================================
  // 🎨 ESTILO DE INPUTS
  // =====================================================

  const inputStyles = {

    "& .MuiInputLabel-root": {
      color: "#aaa"
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#ff6e00"
    },

    "& .MuiOutlinedInput-root": {

      color: "#fff",

      backgroundColor: "#242424",

      "& fieldset": {
        borderColor: "#555"
      },

      "&:hover fieldset": {
        borderColor: "#ff6e00"
      },

      "&.Mui-focused fieldset": {
        borderColor: "#ff6e00"
      }

    }

  };


  // =====================================================
  // 📡 CARGAR NOTICIAS
  // =====================================================

  const fetchNews = async () => {

    try {

      setLoadingNews(true);

      const res = await API.get("/news");

      setNewsList(res.data);

    } catch (error) {

      console.error(
        "Error cargando noticias:",
        error
      );

      setMessage(
        "No se pudieron cargar las noticias."
      );

    } finally {

      setLoadingNews(false);

    }

  };


  // =====================================================
  // 🚀 AL INICIAR
  // =====================================================

  useEffect(() => {

    fetchNews();

  }, []);


  // =====================================================
  // ✏️ CAMBIAR CAMPOS
  // =====================================================

  const handleChange = (field) => (event) => {

    setForm({

      ...form,

      [field]: event.target.value

    });

  };

  const handleImageSelect = (event) => {
  const file = event.target.files?.[0];

  if (!file) return;

  // Verificar que sea una imagen
  if (!file.type.startsWith("image/")) {
    alert("Por favor seleccioná un archivo de imagen.");
    return;
  }

  setSelectedImage(file);

  // Crear preview local
  const previewUrl = URL.createObjectURL(file);

  setForm((prev) => ({
    ...prev,
    image: previewUrl
  }));
};


  // =====================================================
  // 🧰 INSERTAR MARKDOWN
  // =====================================================

  const insertMarkdown = (
    before,
    after = "",
    placeholder = ""
  ) => {

    const textarea =
      contentRef.current;

    if (!textarea) return;

    const start =
      textarea.selectionStart;

    const end =
      textarea.selectionEnd;

    const selectedText =
      form.content.substring(
        start,
        end
      );

    const text =
      selectedText ||
      placeholder;

    const newText =
      before +
      text +
      after;

    const newContent =
      form.content.substring(
        0,
        start
      ) +
      newText +
      form.content.substring(
        end
      );

    setForm({

      ...form,

      content: newContent

    });

    setTimeout(() => {

      textarea.focus();

      const cursorPosition =
        start +
        before.length +
        text.length;

      textarea.setSelectionRange(
        cursorPosition,
        cursorPosition
      );

    }, 0);

  };


  // =====================================================
  // ➕ NUEVA NOTICIA
  // =====================================================

  const handleNewNews = () => {

    setEditingId(null);

    setForm(emptyForm);

    setMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

  };


  // =====================================================
  // ✏️ EDITAR NOTICIA
  // =====================================================

  const handleEdit = (news) => {

    setEditingId(news._id);

    setForm({

      title:
        news.title || "",

      description:
        news.description || "",

      content:
        news.content || "",

      image:
        news.image || ""

    });

    setMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

  };


  // =====================================================
  // ❌ CANCELAR EDICIÓN
  // =====================================================

  const handleCancelEdit = () => {

    setEditingId(null);

    setForm(emptyForm);

    setMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

  };


  // =====================================================
  // 🚀 CREAR / ACTUALIZAR
  // =====================================================

  const handleSubmit = async () => {

    setMessage("");

    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.content.trim()
    ) {

      setMessage(
        "Completá título, descripción y contenido."
      );

      return;

    }

    try {

      setLoading(true);

      const token =
        localStorage.getItem("token");

      const config = {

        headers: {

          Authorization:
            `Bearer ${token}`

        }

      };


      // =================================================
      // ✏️ EDITAR
      // =================================================

      if (editingId) {

        await API.put(
          `/news/${editingId}`,
          form,
          config
        );

        setMessage(
          "Noticia actualizada correctamente 🚀"
        );

      }

      // =================================================
      // ➕ CREAR
      // =================================================

      else {

        await API.post(
          "/news",
          form,
          config
        );

        setMessage(
          "Noticia creada correctamente 🚀"
        );

      }


      // Recargar lista

      await fetchNews();

      // Limpiar editor

      setForm(emptyForm);

      setEditingId(null);

    } catch (error) {

      console.error(
        "Error guardando noticia:",
        error
      );

      console.error(
        "Respuesta del backend:",
        error.response?.data
      );

      setMessage(

        error.response?.data?.message ||

        error.response?.data?.error ||

        "No se pudo guardar la noticia."

      );

    } finally {

      setLoading(false);

    }

  };


  // =====================================================
  // 🗑️ ELIMINAR
  // =====================================================

  const handleDelete = async (id) => {

    const confirmed =
      window.confirm(
        "¿Seguro que querés eliminar esta noticia?\n\nEsta acción no se puede deshacer."
      );

    if (!confirmed) return;


    try {

      const token =
        localStorage.getItem("token");

      await API.delete(
        `/news/${id}`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`
          }
        }
      );


      // Sacar de la lista inmediatamente

      setNewsList(
        prev =>
          prev.filter(
            news =>
              news._id !== id
          )
      );


      // Si estábamos editando
      // esa noticia

      if (editingId === id) {

        setEditingId(null);

        setForm(emptyForm);

      }

      setMessage(
        "Noticia eliminada correctamente."
      );

    } catch (error) {

      console.error(
        "Error eliminando noticia:",
        error
      );

      setMessage(

        error.response?.data?.message ||

        error.response?.data?.error ||

        "No se pudo eliminar la noticia."

      );

    }

  };


  // =====================================================
  // 🖼️ PREVIEW IMAGEN
  // =====================================================

  const imagePreview =
    form.image?.trim();


  // =====================================================
  // 📰 RENDER
  // =====================================================

  return (

    <Box
      sx={{
        minHeight: "100vh",

        background:
          "linear-gradient(180deg, #0a0a0a, #1a1a1a)",

        py: 8
      }}
    >

      <Container maxWidth="lg">


        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            mb: 5,
            textAlign: "center"
          }}
        >

          <Typography
            variant="h3"
            sx={{
              color: "#fff",
              fontWeight: "bold",
              mb: 1
            }}
          >
            Gestión de noticias
          </Typography>

          <Typography
            sx={{
              color: "#aaa"
            }}
          >
            Creá, editá y administrá las noticias de Rebel Fox Gaming.
          </Typography>

        </Box>


        {/* =================================================
            LISTA DE NOTICIAS
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2,
              md: 4
            },

            mb: 5,

            backgroundColor:
              "#181818",

            border:
              "1px solid rgba(255,110,0,0.2)",

            borderRadius:
              "16px",

            boxShadow:
              "0 0 30px rgba(0,0,0,0.4)"
          }}
        >

          <Box
            sx={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              gap: 2,
              mb: 3
            }}
          >

            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                fontWeight: "bold"
              }}
            >
              📰 Noticias publicadas
            </Typography>

            <Button
              variant="contained"
              onClick={handleNewNews}
              sx={{
                background:
                  "linear-gradient(90deg, #ff6e00, #ff9d00)",

                fontWeight: "bold",

                "&:hover": {
                  background:
                    "linear-gradient(90deg, #e65f00, #ff8c00)"
                }
              }}
            >
              ➕ Nueva noticia
            </Button>

          </Box>


          {/* LOADING */}

          {loadingNews && (

            <Typography
              sx={{
                color: "#aaa"
              }}
            >
              Cargando noticias...
            </Typography>

          )}


          {/* SIN NOTICIAS */}

          {!loadingNews &&
            newsList.length === 0 && (

              <Typography
                sx={{
                  color: "#777",
                  py: 3
                }}
              >
                Todavía no hay noticias publicadas.
              </Typography>

            )}


          {/* LISTA */}

          {!loadingNews &&
            newsList.map((news) => (

              <Paper
                key={news._id}
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: "row"
                  },

                  gap: 3,

                  p: 2,

                  mb: 2,

                  backgroundColor:
                    "#222",

                  border:
                    "1px solid rgba(255,255,255,0.08)",

                  borderRadius:
                    "12px"
                }}
              >

                {/* IMAGEN */}

                {news.image && (

                  <Box
                    sx={{
                      width: {
                        xs: "100%",
                        md: "180px"
                      },

                      height: "120px",

                      flexShrink: 0,

                      borderRadius:
                        "8px",

                      overflow:
                        "hidden",

                      backgroundColor:
                        "#111"
                    }}
                  >

                    <img
                      src={news.image}
                      alt={news.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block"
                      }}
                    />

                  </Box>

                )}


                {/* INFORMACIÓN */}

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0
                  }}
                >

                  <Typography
                    sx={{
                      color: "#ff6e00",
                      fontWeight: "bold",
                      fontSize: "1.15rem"
                    }}
                  >
                    {news.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#777",
                      fontSize: "0.8rem",
                      mt: 0.5
                    }}
                  >
                    {new Date(
                      news.createdAt
                    ).toLocaleDateString(
                      "es-AR"
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#bbb",
                      mt: 1,

                      display:
                        "-webkit-box",

                      WebkitLineClamp: 2,

                      WebkitBoxOrient:
                        "vertical",

                      overflow:
                        "hidden"
                    }}
                  >
                    {news.description}
                  </Typography>

                </Box>


                {/* ACCIONES */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,

                    alignSelf: {
                      xs: "flex-end",
                      md: "center"
                    }
                  }}
                >

                  <Tooltip title="Editar">

                    <IconButton
                      onClick={() =>
                        handleEdit(news)
                      }
                      sx={{
                        color: "#ff6e00",

                        backgroundColor:
                          "rgba(255,110,0,0.08)",

                        "&:hover": {
                          backgroundColor:
                            "rgba(255,110,0,0.18)"
                        }
                      }}
                    >
                      ✏️
                    </IconButton>

                  </Tooltip>


                  <Tooltip title="Eliminar">

                    <IconButton
                      onClick={() =>
                        handleDelete(
                          news._id
                        )
                      }
                      sx={{
                        color: "#ff4444",

                        backgroundColor:
                          "rgba(255,68,68,0.08)",

                        "&:hover": {
                          backgroundColor:
                            "rgba(255,68,68,0.18)"
                        }
                      }}
                    >
                      🗑️
                    </IconButton>

                  </Tooltip>

                </Box>

              </Paper>

            ))}

        </Paper>


        {/* =================================================
            EDITOR
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2,
              md: 4
            },

            backgroundColor:
              "#181818",

            border:
              "1px solid rgba(255,110,0,0.2)",

            borderRadius:
              "16px",

            boxShadow:
              "0 0 30px rgba(0,0,0,0.4)"
          }}
        >


          {/* =================================================
              EDITOR HEADER
          ================================================= */}

          <Box
            sx={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: {
                xs: "flex-start",
                md: "center"
              },
              flexDirection: {
                xs: "column",
                md: "row"
              },
              gap: 2,
              mb: 4
            }}
          >

            <Box>

              <Typography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: "bold"
                }}
              >
                {editingId
                  ? "✏️ Editar noticia"
                  : "➕ Nueva noticia"}
              </Typography>

              <Typography
                sx={{
                  color: "#888",
                  mt: 0.5
                }}
              >
                {editingId
                  ? "Modificá el contenido de la noticia."
                  : "Publicá una nueva noticia para Rebel Fox Gaming."}
              </Typography>

            </Box>


            {editingId && (

              <Button
                variant="outlined"
                onClick={
                  handleCancelEdit
                }
                sx={{
                  color: "#fff",
                  borderColor: "#555",

                  "&:hover": {
                    borderColor:
                      "#ff6e00",
                    color:
                      "#ff6e00"
                  }
                }}
              >
                Cancelar edición
              </Button>

            )}

          </Box>


          {/* =================================================
              TÍTULO
          ================================================= */}

          <TextField
            label="Título"
            value={form.title}
            onChange={
              handleChange("title")
            }
            fullWidth
            sx={inputStyles}
          />


          {/* =================================================
              DESCRIPCIÓN
          ================================================= */}

          <TextField
            label="Descripción"
            value={
              form.description
            }
            onChange={
              handleChange(
                "description"
              )
            }
            fullWidth
            sx={{
              ...inputStyles,
              mt: 3
            }}
          />


          {/* =================================================
              IMAGEN
          ================================================= */}

          <TextField
            label="Imagen URL"
            value={form.image}
            onChange={
              handleChange("image")
            }
            fullWidth
            sx={{
              ...inputStyles,
              mt: 3
            }}
            helperText={
              "Pegá una URL directa de una imagen."
            }
            FormHelperTextProps={{
              sx: {
                color: "#777"
              }
            }}
          />

          <Box
  sx={{
    mt: 2,
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexWrap: "wrap"
  }}
>

  <Button
    variant="outlined"
    component="label"
    sx={{
      borderColor: "#ff6e00",
      color: "#ff6e00",

      "&:hover": {
        borderColor: "#ff8c33",
        backgroundColor:
          "rgba(255,110,0,0.08)"
      }
    }}
  >
    📁 Seleccionar imagen

    <input
      type="file"
      accept="image/*"
      hidden
      onChange={handleImageSelect}
    />

  </Button>


  {selectedImage && (

    <Typography
      sx={{
        color: "#aaa",
        fontSize: "0.85rem"
      }}
    >
      Imagen seleccionada:{" "}
      {selectedImage.name}
    </Typography>

  )}

</Box>


          {/* =================================================
              PREVIEW IMAGEN
          ================================================= */}

          {imagePreview && (

            <Box
              sx={{
                mt: 3,

                borderRadius:
                  "12px",

                overflow:
                  "hidden",

                border:
                  "1px solid rgba(255,110,0,0.25)",

                backgroundColor:
                  "#111"
              }}
            >

              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: "350px",
                  objectFit: "cover",
                  display: "block"
                }}
                onError={(e) => {

                  e.currentTarget.style.display =
                    "none";

                }}
              />

            </Box>

          )}


          {/* =================================================
              CONTENIDO
          ================================================= */}

          <Typography
            sx={{
              mt: 4,
              mb: 1,
              color: "#fff",
              fontWeight: "bold",
              fontSize: "1.1rem"
            }}
          >
            Contenido
          </Typography>

          <Typography
            sx={{
              color: "#777",
              fontSize: "0.9rem",
              mb: 2
            }}
          >
            Escribí el contenido y utilizá
            las herramientas de formato.
          </Typography>


          {/* =================================================
              TOOLBAR
          ================================================= */}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,

              p: 1,

              backgroundColor:
                "#202020",

              border:
                "1px solid #444",

              borderBottom:
                "none",

              borderRadius:
                "8px 8px 0 0"
            }}
          >

            <Tooltip title="Negrita">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "**",
                    "**",
                    "texto"
                  )
                }
                sx={{
                  color: "#fff",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                <strong>B</strong>
              </IconButton>

            </Tooltip>


            <Tooltip title="Cursiva">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "*",
                    "*",
                    "texto"
                  )
                }
                sx={{
                  color: "#fff",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                <em>I</em>
              </IconButton>

            </Tooltip>


            <Tooltip title="Título">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "## ",
                    "",
                    "Título"
                  )
                }
                sx={{
                  color: "#fff",
                  fontWeight: "bold",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                H2
              </IconButton>

            </Tooltip>


            <Tooltip title="Lista">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "- ",
                    "",
                    "Elemento"
                  )
                }
                sx={{
                  color: "#fff",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                •
              </IconButton>

            </Tooltip>


            <Tooltip title="Link">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "[",
                    "](https://)",
                    "texto"
                  )
                }
                sx={{
                  color: "#fff",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                🔗
              </IconButton>

            </Tooltip>


            <Tooltip title="Imagen">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "![",
                    "](https://)",
                    "Descripción"
                  )
                }
                sx={{
                  color: "#fff",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                🖼️
              </IconButton>

            </Tooltip>


            <Tooltip title="Separador">

              <IconButton
                size="small"
                onClick={() =>
                  insertMarkdown(
                    "\n---\n",
                    "",
                    ""
                  )
                }
                sx={{
                  color: "#fff",

                  "&:hover": {
                    backgroundColor:
                      "rgba(255,110,0,0.15)"
                  }
                }}
              >
                —
              </IconButton>

            </Tooltip>

          </Box>


          {/* =================================================
              TEXTAREA
          ================================================= */}

          <TextField
            inputRef={
              contentRef
            }

            value={
              form.content
            }

            onChange={
              handleChange(
                "content"
              )
            }

            fullWidth

            multiline

            minRows={14}

            placeholder={`# Título de sección

Escribí acá el contenido de la noticia.

**Texto en negrita**

- Punto uno
- Punto dos
- Punto tres`}

            sx={{
              ...inputStyles,

              "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "0 0 8px 8px"
                },

              "& textarea": {
                fontFamily:
                  "monospace",

                lineHeight: 1.7
              }
            }}
          />


          {/* =================================================
              PREVIEW
          ================================================= */}

          <Divider
            sx={{
              my: 5,
              borderColor:
                "rgba(255,255,255,0.08)"
            }}
          />

          <Typography
            sx={{
              color: "#fff",
              fontWeight: "bold",
              fontSize: "1.2rem",
              mb: 2
            }}
          >
            👁 Vista previa
          </Typography>


          <Box
            sx={{
              background:
                "linear-gradient(180deg, #0a0a0a, #141414)",

              borderRadius:
                "12px",

              p: {
                xs: 2,
                md: 4
              },

              border:
                "1px solid rgba(255,110,0,0.15)",

              color: "#ccc",

              "& h1": {
                color: "#fff",
                fontSize: {
                  xs: "2rem",
                  md: "2.7rem"
                },
                marginBottom:
                  "1rem"
              },

              "& h2": {
                color: "#ff6e00",
                fontSize: {
                  xs: "1.6rem",
                  md: "2rem"
                },
                marginTop:
                  "2rem",
                marginBottom:
                  "1rem"
              },

              "& h3": {
                color: "#fff",
                marginTop:
                  "1.5rem"
              },

              "& p": {
                lineHeight: 1.9,
                fontSize:
                  "1.05rem",
                marginBottom:
                  "1rem"
              },

              "& strong": {
                color: "#fff"
              },

              "& a": {
                color: "#ff6e00",
                textDecoration:
                  "none"
              },

              "& ul, & ol": {
                paddingLeft:
                  "2rem",
                marginBottom:
                  "1.5rem"
              },

              "& li": {
                marginBottom:
                  "0.5rem",
                lineHeight: 1.7
              },

              "& blockquote": {
                borderLeft:
                  "4px solid #ff6e00",
                marginLeft: 0,
                paddingLeft:
                  "1rem",
                color: "#aaa",
                fontStyle:
                  "italic"
              },

              "& code": {
                backgroundColor:
                  "#222",
                padding:
                  "3px 6px",
                borderRadius:
                  "4px"
              },

              "& img": {
                display: "block",
                maxWidth:
                  "100%",
                maxHeight:
                  "500px",
                width: "auto",
                height: "auto",
                margin:
                  "2rem auto",
                borderRadius:
                  "12px",
                objectFit:
                  "contain",
                boxShadow:
                  "0 0 20px rgba(255,110,0,0.15)"
              },

              "& hr": {
                border: 0,
                borderTop:
                  "1px solid rgba(255,255,255,0.1)",
                margin:
                  "2.5rem 0"
              }

            }}
          >


            {/* IMAGEN */}

            {imagePreview && (

              <Box
                sx={{
                  mb: 4,
                  borderRadius:
                    "12px",
                  overflow:
                    "hidden"
                }}
              >

                <img
                  src={imagePreview}
                  alt={
                    form.title
                  }
                  style={{
                    width: "100%",
                    maxHeight:
                      "400px",
                    objectFit:
                      "cover",
                    display:
                      "block"
                  }}
                />

              </Box>

            )}


            {/* TÍTULO */}

            {form.title && (

              <Typography
                variant="h3"
                sx={{
                  color: "#fff",
                  fontWeight:
                    "bold",
                  mb: 1
                }}
              >
                {form.title}
              </Typography>

            )}


            {/* DESCRIPCIÓN */}

            {form.description && (

              <Typography
                sx={{
                  color: "#999",
                  fontSize:
                    "1rem",
                  mb: 4,
                  fontStyle:
                    "italic"
                }}
              >
                {form.description}
              </Typography>

            )}


            {/* CONTENIDO */}

            {form.content ? (

              <ReactMarkdown
                remarkPlugins={[
                  remarkGfm
                ]}
                rehypePlugins={[
                  rehypeSanitize
                ]}
              >
                {form.content}
              </ReactMarkdown>

            ) : (

              <Typography
                sx={{
                  color: "#666",
                  fontStyle:
                    "italic"
                }}
              >
                La vista previa aparecerá acá mientras escribís...
              </Typography>

            )}

          </Box>


          {/* =================================================
              MENSAJE
          ================================================= */}

          {message && (

            <Typography
              sx={{
                mt: 3,

                color:
                  message.includes(
                    "correctamente"
                  )
                    ? "#66bb6a"
                    : "#ff6e00",

                fontWeight:
                  "bold"
              }}
            >
              {message}
            </Typography>

          )}


          {/* =================================================
              BOTÓN GUARDAR
          ================================================= */}

          <Button
            variant="contained"
            onClick={
              handleSubmit
            }
            disabled={
              loading
            }
            fullWidth
            sx={{
              mt: 4,
              py: 1.5,

              background:
                "linear-gradient(90deg, #ff6e00, #ff9d00)",

              fontWeight:
                "bold",

              fontSize:
                "1rem",

              "&:hover": {
                background:
                  "linear-gradient(90deg, #e65f00, #ff8c00)"
              }
            }}
          >

            {loading

              ? "Guardando..."

              : editingId

                ? "💾 Guardar cambios"

                : "🚀 Publicar noticia"}

          </Button>


        </Paper>

      </Container>

    </Box>

  );

};

export default AdminNews;